import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';

interface Report {
  id: string;
  targetType: string;
  targetId: string;
  reason: string;
  note?: string;
  status: 'open' | 'closed';
  createdAt: string;
  reporterId: string;
  reporterName: string;
  reporterEmail: string;
}

export default function AdminReports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'open' | 'closed' | 'all'>('open');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  
  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      try {
        const res = await api.admin.getReports({ status: filter === 'all' ? undefined : filter });
        setReports(res);
      } catch (err) {
        console.error('Failed to load reports:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadReports();
  }, [filter]);
  
  const handleResolve = async (id: string, action: 'resolve' | 'dismiss') => {
    try {
      await api.admin.resolveReport(id, { action });
      setReports((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: 'closed' as const } : r))
      );
      setSelectedReport(null);
    } catch (err) {
      console.error('Failed to resolve report:', err);
    }
  };
  
  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      spam: 'Spam',
      harassment: 'Harassment',
      hate_speech: 'Hate Speech',
      violence: 'Violence',
      inappropriate: 'Inappropriate Content',
      misinformation: 'Misinformation',
      other: 'Other',
    };
    return labels[reason] || reason;
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'closed':
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400';
    }
  };
  
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">User Reports</h1>
          <p className="text-slate-500 dark:text-slate-400">
            {reports.filter((r) => r.status === 'open').length} open reports
          </p>
        </div>
        
        <div className="flex gap-2">
          {(['open', 'closed', 'all'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-primary-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-2" />
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full" />
            </div>
          ))}
        </div>
      ) : reports.length === 0 ? (
        <div className="card p-12 text-center">
          <CheckIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">No reports</h2>
          <p className="text-slate-500 dark:text-slate-400">
            {filter === 'open' ? 'All reports have been handled' : 'No reports found'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reports list */}
          <div className="lg:col-span-2 space-y-3">
            {reports.map((report) => (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report)}
                className={`card p-4 w-full text-left transition-all ${
                  selectedReport?.id === report.id
                    ? 'ring-2 ring-primary-500'
                    : 'hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(report.status)}`}>
                      {report.status}
                    </span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {report.targetType}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {formatDateTime(new Date(report.createdAt))}
                  </span>
                </div>
                <p className="font-medium text-slate-900 dark:text-white mb-1">
                  {getReasonLabel(report.reason)}
                </p>
                {report.note && (
                  <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                    {report.note}
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  Reported by {report.reporterName}
                </p>
              </button>
            ))}
          </div>
          
          {/* Detail panel */}
          <div className="lg:col-span-1">
            {selectedReport ? (
              <div className="card p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  Report Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Reason
                    </h4>
                    <p className="text-slate-900 dark:text-white">
                      {getReasonLabel(selectedReport.reason)}
                    </p>
                  </div>
                  
                  {selectedReport.note && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Note
                      </h4>
                      <p className="text-slate-900 dark:text-white text-sm p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        {selectedReport.note}
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Reporter
                    </h4>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {selectedReport.reporterName}
                    </p>
                    <p className="text-xs text-slate-500">
                      {selectedReport.reporterEmail}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Target
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {selectedReport.targetType}: {(selectedReport.targetId || '').substring(0, 8)}...
                    </p>
                  </div>
                  
                  {selectedReport.status === 'open' && (
                    <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
                      <button
                        onClick={() => handleResolve(selectedReport.id, 'resolve')}
                        className="btn btn-primary w-full"
                      >
                        Take Action & Resolve
                      </button>
                      <button
                        onClick={() => handleResolve(selectedReport.id, 'dismiss')}
                        className="btn btn-secondary w-full"
                      >
                        Dismiss Report
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card p-12 text-center">
                <p className="text-slate-500 dark:text-slate-400">
                  Select a report to view details
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
